import React from 'react';
import { Package, AlertCircle } from 'lucide-react';

const InventoryInvestmentPanel = ({ data, onChange, calculations, formatCurrency }) => {
  const parseNum = (val) => {
    const num = parseFloat(String(val)?.replace(/[^\d.-]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const formatInput = (val) => {
    if (!val) return '';
    return String(val);
  };

  const supplierPaymentOptions = [
    { value: 0, label: 'Immediate Payment' },
    { value: 15, label: 'Net 15' },
    { value: 30, label: 'Net 30' },
    { value: 45, label: 'Net 45' },
    { value: 60, label: 'Net 60' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
          <Package className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900">Inventory Investment</h3>
          <p className="text-xs text-slate-600">Capital requirements for inventory</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Product Cost per Unit
          </label>
          <input
            type="text"
            value={formatInput(data?.productCost)}
            onChange={(e) => onChange(null, 'productCost', e?.target?.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
            placeholder="Enter unit cost"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Quantity Needed (Monthly)
          </label>
          <input
            type="text"
            value={formatInput(data?.quantityNeeded)}
            onChange={(e) => onChange(null, 'quantityNeeded', e?.target?.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
            placeholder="Enter quantity"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Supplier Payment Terms
          </label>
          <select
            value={data?.supplierPaymentTerms}
            onChange={(e) => onChange(null, 'supplierPaymentTerms', parseInt(e?.target?.value))}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
          >
            {supplierPaymentOptions?.map(opt => (
              <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Lead Time (Days)
            </label>
            <input
              type="number"
              value={data?.leadTimeDays}
              onChange={(e) => onChange(null, 'leadTimeDays', e?.target?.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
              min="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Safety Stock %
            </label>
            <input
              type="number"
              value={data?.safetyStockPercent}
              onChange={(e) => onChange(null, 'safetyStockPercent', e?.target?.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
              min="0"
              max="100"
            />
          </div>
        </div>

        {/* Investment Summary */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <h4 className="text-sm font-bold text-slate-900 mb-4">Investment Summary</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Total Investment</span>
              <strong className="text-base text-slate-900">
                {formatCurrency(calculations?.totalInventoryInvestment)}
              </strong>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Safety Stock Units</span>
              <strong className="text-base text-slate-900">
                {calculations?.safetyStockUnits} units
              </strong>
            </div>

            <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg border border-amber-200">
              <span className="text-sm font-medium text-amber-900">Reorder Point</span>
              <strong className="text-base text-amber-700">
                {calculations?.reorderPoint} units
              </strong>
            </div>
          </div>
        </div>

        {/* Cash Flow Impact Alert */}
        {calculations?.totalInventoryInvestment > parseNum(data?.currentCash) && (
          <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-900 mb-1">Insufficient Cash</p>
              <p className="text-xs text-red-700">
                Inventory investment exceeds current cash position. Consider reducing order quantity or securing additional funding.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryInvestmentPanel;